import React, { type FunctionComponent, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AsyncStatus, type AsyncDataState, type DropdownOption } from 'legacy/common/types'
import withApi, { type WithApiInjectedProps } from 'legacy/common/withApi'
import { useRetailerProductAggregateDataMesh } from 'legacy/sections/catalog/graphql/retailerProductAggregateDataMesh.hooks'
import { errors } from 'utils/error-handling/errors'
import {
  withNotificationsContext,
  type NotificationsContextValue,
} from 'legacy/contexts/notifications/NotificationsContext'
import {
  type ProductResponseBody,
  type ProductFormValues,
  type ProductUpdateRequestBody,
  type ProductImageDeleteBody,
} from 'legacy/routes/catalog/types/product.types'
import { type Brands } from 'legacy/routes/catalog/types/brands.types'
import {
  deserializeProductValues,
  extractDeletedImageAnglesFromValues,
  transformFormValuesToRequestBody,
} from './productUtils'

interface ChildFunctionValues extends State {
  onUpdate: (products: ProductFormValues) => void
  availabilityLoading: boolean
}

interface Props extends WithApiInjectedProps, NotificationsContextValue {
  partner_id: string
  warehouse_id: string
  product_id: string
  children: (values: ChildFunctionValues) => React.ReactNode
}

interface State {
  brands: AsyncDataState<DropdownOption<number>[]>
  product: AsyncDataState<ProductFormValues>
  updateProductStatus: AsyncStatus
  updateItemsStatus: AsyncStatus
  availableItemsCount: number
}

export const ProductContainer: FunctionComponent<Props> = ({
  partner_id,
  warehouse_id,
  product_id,
  children,
  api,
  notify,
  notifyError,
}) => {
  const [product, setProduct] = useState<AsyncDataState<ProductFormValues>>({
    status: AsyncStatus.Busy,
    data: null,
  })
  const [brands, setBrands] = useState<AsyncDataState<DropdownOption<number>[]>>({
    status: AsyncStatus.Busy,
    data: null,
  })
  const [updateProductStatus, setUpdateProductStatus] = useState<AsyncStatus>(AsyncStatus.Initial)
  const [updateItemsStatus, setUpdateItemsStatus] = useState<AsyncStatus>(AsyncStatus.Initial)

  useEffect(() => {
    fetchProduct(partner_id, warehouse_id, product_id)
    fetchBrands(partner_id, warehouse_id)
  }, [])

  const transformResponseBodyToFormValues = (product: ProductResponseBody): ProductFormValues => {
    return deserializeProductValues(product)
  }

  const transformBrandsResponseBodyToOptions = (options: Brands): DropdownOption<number>[] =>
    options.map(({ id, name }) => ({ value: id, label: name }))

  const fetchProduct = async (partner_id: string, warehouse_id: string, product_id: string) => {
    try {
      const { data }: { data: ProductResponseBody } = await api.get(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/${product_id}`
      )
      setProduct({
        status: AsyncStatus.Done,
        data: transformResponseBodyToFormValues(data),
      })
    } catch (e) {
      errors.captureWarning(e)

      setProduct({
        status: AsyncStatus.Fail,
        data: null,
      })
    }
  }

  const { apiLoading: availabilityLoading, apiResult } = useRetailerProductAggregateDataMesh({
    retailerId: warehouse_id,
    productIds: [product_id],
  })

  const availableItemsCount = parseInt(apiResult?.retailerProductData?.[0].availableItemsCount, 10)

  const fetchBrands = async (partner_id: string, warehouse_id: string) => {
    try {
      const { data }: { data: Brands } = await api.get(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/brands`
      )
      setBrands({
        status: AsyncStatus.Done,
        data: transformBrandsResponseBodyToOptions(data),
      })
    } catch (e) {
      errors.captureWarning(e)

      setBrands({
        status: AsyncStatus.Fail,
        data: null,
      })
    }
  }

  const requestUpdate = async (values: ProductFormValues) => {
    const { data } = product
    if (!data) {
      return
    }

    setUpdateProductStatus(AsyncStatus.Busy)

    const requestBody = transformFormValuesToRequestBody(data, values)

    const deletedImageAngles = extractDeletedImageAnglesFromValues(data, values)

    try {
      await api.put<{}, ProductUpdateRequestBody>(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/${product_id}`,
        {
          data: requestBody,
        }
      )

      const newSecondaryImages = values.secondary_images

      try {
        if (deletedImageAngles.length > 0) {
          await api.delete<{}, ProductImageDeleteBody>(
            `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/${product_id}/image`,
            {
              data: { image_angles: deletedImageAngles },
            }
          )
        }

        notify(<FormattedMessage id="catalog.product.edit.notification" />)
      } catch (e) {
        notifyError(<FormattedMessage id="catalog.product.edit.secondary_image_delete_fail" />)

        const initialSecondaryImages = data.secondary_images

        deletedImageAngles.forEach(angle => {
          const restoreImage = initialSecondaryImages[angle] || undefined

          newSecondaryImages[angle] = restoreImage
        })
      }

      setUpdateProductStatus(AsyncStatus.Done)
      setProduct({
        data: { ...values, secondary_images: newSecondaryImages },
        status: AsyncStatus.Done,
      })
    } catch (e) {
      errors.captureWarning(e)

      setUpdateProductStatus(AsyncStatus.Fail)
    }
  }

  const handleUpdate = (product: ProductFormValues) => {
    requestUpdate(product)
  }

  return (
    <>
      {children({
        availabilityLoading,
        product,
        updateProductStatus,
        updateItemsStatus,
        brands,
        availableItemsCount,
        onUpdate: handleUpdate,
      })}
    </>
  )
}

export default withNotificationsContext(withApi(ProductContainer))
